import React, { useEffect, useRef, useState } from "react"
import LineChart from "./LineChart"
import GenericChart from "./GenericChart"
import { Button, Card, CardBody, Col, Row } from "reactstrap"
import Select from "react-select"
import { customStyles } from "utils/SelectStyles"
import { FaFilter } from "react-icons/fa"
import Breadcrumb from "components/Common/Breadcrumb"
import { DatePicker } from "antd"
import { months } from "utils/defaultpositions"
import Loader from "components/Loader/loader"
import { FetchPostData } from "utils/Common_api_functions"
import secureLocalStorage from "react-secure-storage"
import { MdDownload } from "react-icons/md"
import html2canvas from "html2canvas"
import jsPDF from "jspdf"
import Swal from "sweetalert2"
import dayjs from "dayjs"

const { RangePicker } = DatePicker

const currentYear = new Date().getFullYear()
const years = Array.from({ length: currentYear - 2020 }, (_, i) => ({
  label: `${2021 + i}`,
  value: 2021 + i,
}))?.reverse()

// Each chart gets its own color
const CHART_COLORS = {
  oee: "#6C63FF", // purple   — efficiency
  production: "#10B981", // emerald  — production volume
  waste: "#F97316", // orange   — waste
  dispatch: "#EF5350", // red      — returns/OOD
  direct: "#FFCA28", // amber    — direct labour
  indirect: "#26A69A", // teal     — indirect labour
  downtime: "#EC407A", // pink     — downtime
}

const OEEIndex = () => {
  const [loading, setLoading] = useState(false)
  const [oeeData, setOEEData] = useState({})
  const [dateRange, setDateRange] = useState([])
  const [selectedMonths, setSelectedMonths] = useState([])
  const [selectedYears, setSelectedYears] = useState([])

  const [sortOee, setSortOee] = useState("chronological")
  const [sortProduction, setSortProduction] = useState("chronological")
  const [sortWaste, setSortWaste] = useState("chronological")
  const [sortDispatch, setSortDispatch] = useState("chronological")
  const [sortDirect, setSortDirect] = useState("chronological")
  const [sortIndirect, setSortIndirect] = useState("chronological")
  const [sortDowntime, setSortDowntime] = useState("chronological")

  const [maxDate, setMaxDate] = useState("")

  const [currentweek, setCurrentWeek] = useState([])

  useEffect(() => {
    const Years = secureLocalStorage?.getItem("Years")
    const Months = secureLocalStorage?.getItem("Months")
    const DateRange = secureLocalStorage?.getItem("DateRange")
    const FinalRangeRaw = secureLocalStorage?.getItem("FINALRANGE")

    setSelectedYears(Years)
    setSelectedMonths(Months)

    const activeDateRaw = DateRange ?? FinalRangeRaw
    setDateRange(activeDateRaw ? activeDateRaw.map(d => dayjs(d)) : [])
    setMaxDate(FinalRangeRaw?.[1] ? dayjs(FinalRangeRaw[1]) : null)
    setCurrentWeek(activeDateRaw ? activeDateRaw.map(d => dayjs(d)) : [])

    const fetchInitialData = async () => {
      const activeDateRange = activeDateRaw
        ? activeDateRaw.map(d => dayjs(d))
        : []

      const payload = {
        startDate: activeDateRange?.[0]?.isValid()
          ? activeDateRange[0].format("YYYY-MM-DD")
          : undefined,
        endDate: activeDateRange?.[1]?.isValid()
          ? activeDateRange[1].format("YYYY-MM-DD")
          : undefined,
        month: Months?.map(i => i?.value)?.join(",") || undefined,
        year: Years?.map(i => i?.value)?.join(",") || undefined,
      }

      const APidata = await FetchPostData(setLoading, "sheet/oee", payload)
      if (APidata?.data?.success) setOEEData(APidata?.data)
      else {
        setOEEData({})
        console.log(APidata?.response)
      }
    }

    fetchInitialData()
  }, [])

  const handleFilter = async () => {
    const APidata = await FetchPostData(setLoading, "sheet/oee", {
      startDate: dateRange?.[0]?.format("YYYY-MM-DD"),
      endDate: dateRange?.[1]?.format("YYYY-MM-DD"),
      month: selectedMonths?.map(item => item?.value)?.join(","),
      year: selectedYears?.map(item => item?.value)?.join(","),
    })
    if (APidata?.data?.success) setOEEData(APidata?.data)
    else {
      setOEEData({})
      console.log(APidata?.response)
    }
  }

  const dashboardRef = useRef(null)

  const handleExport = async () => {
    const element = dashboardRef.current
    if (!element) return

    try {
      setLoading(true)

      const originalHeight = element.style.height
      const originalOverflow = element.style.overflow
      element.style.height = "auto"
      element.style.overflow = "visible"

      // ✅ Get the element's actual position on the page
      const rect = element.getBoundingClientRect()
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      const elementTop = rect.top + scrollTop // absolute top position of dashboardRef

      const canvas = await html2canvas(element, {
        scale: 1.2,
        useCORS: true,
        allowTaint: true,
        scrollX: 0,
        scrollY: -scrollTop, // ✅ This corrects the scroll offset
        y: 0, // ✅ Start capturing from top of the element itself
        x: 0,
        windowWidth: element.scrollWidth,
        windowHeight: element.scrollHeight,
        logging: false,
      })

      element.style.height = originalHeight
      element.style.overflow = originalOverflow

      const imgData = canvas.toDataURL("image/jpeg", 0.75)

      const imgWidth = canvas.width / 1.2
      const imgHeight = canvas.height / 1.2

      const marginLeft = 30
      const marginRight = 30
      const marginTop = 10
      const marginBottom = 20

      const pdfWidth = imgWidth + marginLeft + marginRight
      const pdfHeight = imgHeight + marginTop + marginBottom

      const pdf = new jsPDF({
        orientation: pdfWidth > pdfHeight ? "landscape" : "portrait",
        unit: "px",
        format: [pdfWidth, pdfHeight],
        compress: true,
      })

      pdf.addImage(
        imgData,
        "JPEG",
        marginLeft,
        marginTop,
        imgWidth,
        imgHeight,
        undefined,
        "FAST"
      )

      pdf.save(
        `OEE_Charts${new Date()
          .toLocaleDateString("en-GB")
          .replace(/\//g, "-")}.pdf`
      )
    } catch (e) {
      Swal.fire({
        title: "Export Failed",
        text: "Something went wrong while exporting.",
        icon: "error",
      })
    } finally {
      setLoading(false)
    }
  }

  const showFixedWeeks =
    selectedYears?.length > 0 && !selectedMonths?.length && dateRange === null

  return (
    <div className="page-content" ref={dashboardRef}>
      {loading && <Loader />}

      {/* ── Filter Header ─────────────────────────────────────────── */}
      <div
        style={{
          background: "linear-gradient(135deg, #ffffff 0%, #f8faff 100%)",
          borderRadius: "16px",
          border: "1px solid #ede9fe",
          boxShadow: "0 4px 24px rgba(108,99,255,0.07)",
          padding: "16px 20px",
          marginBottom: "24px",
          position: "relative",
        }}
      >
        <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div>
              <h4
                style={{
                  margin: 0,
                  fontSize: "18px",
                  fontWeight: 700,
                  color: "#000",
                }}
              >
                OEE Charts
              </h4>
              <p style={{ margin: 0, fontSize: "12px", color: "#666" }}>
                Overall Equipment Effectiveness
              </p>
            </div>
            <div
              style={{ width: "1px", height: "36px", background: "#e2e8f0" }}
            />

            {/* Export button */}
            <Button
              onClick={handleExport}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                background: "#0f172a",
                border: "none",
                borderRadius: "10px",
                padding: "8px 16px",
                fontSize: "13px",
                fontWeight: 600,
                color: "#fff",
                cursor: "pointer",
                letterSpacing: "0.2px",
              }}
            >
              <MdDownload size={15} />
              Export
            </Button>
            {currentweek?.[0] && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  background: "#f0fdf4",
                  border: "1px solid #bbf7d0",
                  borderRadius: "10px",
                  padding: "6px 12px",
                }}
              >
                <span
                  style={{
                    width: "7px",
                    height: "7px",
                    borderRadius: "50%",
                    background: "#16a34a",
                    display: "inline-block",
                    boxShadow: "0 0 0 2px #bbf7d0",
                  }}
                />
                <div>
                  <div
                    style={{
                      fontSize: "10px",
                      color: "#15803d",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.6px",
                    }}
                  >
                    Latest week Upload
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#166534",
                      fontWeight: 600,
                      marginTop: "1px",
                    }}
                  >
                    {currentweek[0].format("DD MMM")} —{" "}
                    {currentweek[1].format("DD MMM YYYY")}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="d-flex align-items-end gap-2 flex-wrap">
            <RangePicker
              value={dateRange}
              placeholder={["Start (Sat)", "End (Fri)"]}
              onChange={values => setDateRange(values)}
              format="DD-MM-YYYY"
              style={{ borderRadius: "8px", height: "38px" }}
              disabledDate={current => {
                return current && current > maxDate.endOf("day")
              }}
            />
            <Select
              placeholder="Month"
              options={months}
              styles={customStyles}
              isMulti
              value={selectedMonths}
              onChange={v => {
                setSelectedMonths(v)
                if (!v || v.length === 0) {
                  setSortOee("chronological")
                  setSortProduction("chronological")
                  setSortWaste("chronological")
                  setSortDispatch("chronological")
                  setSortDirect("chronological")
                  setSortIndirect("chronological")
                  setSortDowntime("chronological")
                }
              }}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
            <Select
              placeholder="Year"
              options={years}
              styles={customStyles}
              isMulti
              value={selectedYears}
              onChange={v => {
                setSelectedYears(v)
                if (!v || v.length === 0) {
                  setSortOee("chronological")
                  setSortProduction("chronological")
                  setSortWaste("chronological")
                  setSortDispatch("chronological")
                  setSortDirect("chronological")
                  setSortIndirect("chronological")
                  setSortDowntime("chronological")
                }
              }}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
            <Button
              onClick={handleFilter}
              className="btn-blue rounded-2 border-transparent"
            >
              <FaFilter size={12} />
            </Button>
          </div>
        </div>
      </div>

      {/* ── Charts Grid ───────────────────────────────────────────── */}
      <Row className="g-3">
        <Col xl={12} className="over-graph">
          <LineChart
            title="OEE Efficiency"
            data={oeeData.oee}
            previousData={oeeData.oeePreviousYear}
            xKey="date"
            yKey="efficiency"
            color={CHART_COLORS.oee}
            fixedWeeks={showFixedWeeks}
            sortOrder={sortOee}
            onSortOrderChange={setSortOee}
          />
        </Col>
        <Col xl={12}>
          <LineChart
            title="Total Sausage Production"
            data={oeeData.totalSausageProduction}
            previousData={oeeData.totalSausageProductionPreviousYear}
            xKey="date"
            yKey="quantity"
            color={CHART_COLORS.production}
            fixedWeeks={showFixedWeeks}
            sortOrder={sortProduction}
            onSortOrderChange={setSortProduction}
          />
        </Col>
        <Col xl={12}>
          <LineChart
            title="Production KG Waste"
            data={oeeData.wasteExcludeFinishedProduct}
            previousData={oeeData.wasteExcludeFinishedProductPreviousYear}
            xKey="date"
            yKey="quantity"
            color={CHART_COLORS.waste}
            fixedWeeks={showFixedWeeks}
            sortOrder={sortWaste}
            onSortOrderChange={setSortWaste}
          />
        </Col>
        <Col xl={12}>
          <LineChart
            title="Dispatch OOD / Return Kgs"
            data={oeeData.finishedProductWaste}
            previousData={oeeData.finishedProductWastePreviousYear}
            xKey="date"
            yKey="quantity"
            color={CHART_COLORS.dispatch}
            fixedWeeks={showFixedWeeks}
            sortOrder={sortDispatch}
            onSortOrderChange={setSortDispatch}
          />
        </Col>
        <Col xl={12}>
          <LineChart
            title="Direct Labour Cost £/kg"
            data={oeeData.directLabourCost}
            previousData={oeeData.directLabourCostPreviousYear}
            xKey="date"
            yKey="cost"
            color={CHART_COLORS.direct}
            fixedWeeks={showFixedWeeks}
            yPrefix="£"
            sortOrder={sortDirect}
            onSortOrderChange={setSortDirect}
          />
        </Col>
        <Col xl={12}>
          <LineChart
            title="Indirect Labour Cost £/kg"
            data={oeeData.inDirectLabourCost}
            previousData={oeeData.inDirectLabourCostPreviousYear}
            xKey="date"
            yKey="cost"
            color={CHART_COLORS.indirect}
            fixedWeeks={showFixedWeeks}
            yPrefix="£"
            sortOrder={sortIndirect}
            onSortOrderChange={setSortIndirect}
          />
        </Col>
        <Col xl={12}>
          <LineChart
            title="Downtime — Lost Mins"
            data={oeeData.downtime}
            previousData={oeeData.downtimePreviousYear}
            xKey="date"
            yKey="minutes"
            color={CHART_COLORS.downtime}
            fixedWeeks={showFixedWeeks}
            sortOrder={sortDowntime}
            onSortOrderChange={setSortDowntime}
          />
        </Col>

        {/* <Col xl={12}>
              <GenericChart
                title="Revenue by Kilo"
                type="line"
                series={[
                  {
                    name: "Revenue",
                    data: [3.2, 3.4, 3.3, 3.6, 3.5, 3.1, 3.4],
                  },
                ]}
              />
            </Col> */}
      </Row>
    </div>
  )
}

export default OEEIndex
